import classNames from 'classnames';
import React from 'react';
import { ClipLoader } from 'react-spinners';
import { useAsyncButton } from '../../hooks/use-async-button';
import UDTippy, { UDTippyProps } from '../tippy';
import { IconButtonBlock } from './styles';

export type IconButtonVariant = 'default' | 'primary' | 'success' | 'danger' | 'ghost';
export type IconButtonSize = 'sm' | 'md' | 'lg';

export interface UDIconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'onClick'> {
  /** Иконка (обычно из lucide-react). Размер задаётся пропом size, а не самой иконкой. */
  icon: JSX.Element;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  /** Внешний индикатор загрузки. Асинхронный onClick подсвечивается автоматически. */
  loading?: boolean;
  /** Подсказка. Без неё смысл иконки не очевиден — заполняйте всегда, когда кнопка без подписи. */
  tooltip?: React.ReactNode;
  tooltipPlacement?: UDTippyProps['placement'];
  onClick?: (...args: any[]) => void | Promise<any>;
}

const loaderSizeBySize: Record<IconButtonSize, number> = {
  sm: 14,
  md: 18,
  lg: 22,
};

/**
 * Кнопка-плашка с одной иконкой: белый фон, серый бордер, цвет иконки по варианту.
 * На hover плашка заливается цветом варианта.
 *
 * Вариант `ghost` — без плашки и бордера (для плотных тулбаров): на hover меняется
 * только цвет иконки.
 */
const UDIconButton = React.forwardRef<HTMLButtonElement, UDIconButtonProps>((props, ref) => {
  const {
    icon,
    variant = 'default',
    size = 'md',
    loading: externalLoading,
    tooltip,
    tooltipPlacement = 'top',
    className,
    disabled,
    type = 'button',
    onClick,
    ...otherProps
  } = props;

  const { isLoading, handleClick } = useAsyncButton(onClick);
  const isLoadingState = isLoading || externalLoading;

  const button = (
    <IconButtonBlock
      {...otherProps}
      ref={ref}
      type={type}
      className={classNames(variant, size, { loading: isLoadingState }, className)}
      disabled={disabled || isLoadingState}
      onClick={handleClick}
    >
      {isLoadingState ? <ClipLoader size={loaderSizeBySize[size]} color="currentColor" /> : icon}
    </IconButtonBlock>
  );

  if (!tooltip) {
    return button;
  }

  // обёртка нужна, чтобы подсказка работала и на disabled-кнопке (она не отдаёт mouse-события)
  return (
    <UDTippy content={tooltip} placement={tooltipPlacement}>
      <span className="inline-flex">{button}</span>
    </UDTippy>
  );
});

export default UDIconButton;
